import React from "react";
import { Box, Typography, IconButton, Button, useTheme, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Logo from "../../../shared/Logo";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { getSymbolImageUrl } from "../../../../lib/symbolImage";

// Flat UI Color System - Theme Aware (matching ViewTicker pattern)
const getFlatUIColors = (theme: any) => {
  const isDark = theme.palette.mode === "dark";

  return {
    background: isDark ? "#1a1a1a" : "#ffffff",
    surface: isDark ? "#212121" : "#f8fafc",
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
    textPrimary: isDark ? "#e4e6ea" : "#0f172a",
    textSecondary: isDark ? "#9ca3af" : "#64748b",
  };
};

// Flat UI Typography System
const flatUITypography = {
  body: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.4,
  },
  label: {
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
};

interface CompactProjectionsHeaderProps {
  ticker: string;
  companyName: string;
  exchange?: string;
  currentPrice?: number;
  fairValue?: number | null;
  upside: number | null;
  onBack: () => void;
  fairValueLabel?: string;
  onHelpClick?: () => void;
}

const CompactProjectionsHeader: React.FC<CompactProjectionsHeaderProps> = ({
  ticker,
  companyName,
  exchange,
  currentPrice,
  fairValue,
  upside,
  onBack,
  fairValueLabel = "Calculated Fair Value",
  onHelpClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const flatUIColors = getFlatUIColors(theme);

  // Get currency-aware formatting from context
  // formatPrice: for raw API prices (converts pence to pounds for UK stocks)
  // formatDisplayPrice: for calculated values already in display currency
  const { formatPrice, formatDisplayPrice } = useStockCurrency();

  const isOvervalued = upside !== null && upside < 0;
  const isUndervalued = upside !== null && upside > 0;

  // Determine color based on valuation
  const valuationColor = isOvervalued
    ? "#ef4444"
    : isUndervalued
    ? "#22c55e"
    : flatUIColors.textSecondary;

  return (
    <Box
      sx={{
        height: isMobile ? "auto" : 64,
        minHeight: isMobile ? 56 : 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: isMobile ? 2 : 3,
        py: isMobile ? 1 : 0,
        borderBottom: `1px solid ${flatUIColors.border}`,
        backgroundColor: flatUIColors.background,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Left Section - Company Info */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 1 : 2,
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* Back Button - Desktop only */}
        {!isMobile && (
          <IconButton
            size="small"
            onClick={onBack}
            sx={{
              color: flatUIColors.textSecondary,
              "&:hover": {
                backgroundColor: flatUIColors.surface,
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        {/* Company Logo */}
        <Logo
          src={getSymbolImageUrl(ticker)}
          alt={`${companyName} logo`}
          ticker={ticker}
          size={isMobile ? 32 : 40}
        />

        {/* Company Name and Ticker */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
          <Typography
            sx={{
              ...flatUITypography.body,
              fontWeight: 600,
              color: flatUIColors.textPrimary,
              lineHeight: 1,
              fontSize: isMobile ? "0.8rem" : "1rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {companyName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              sx={{
                ...flatUITypography.caption,
                color: flatUIColors.textSecondary,
                fontWeight: 500,
                fontSize: isMobile ? "0.7rem" : "0.875rem",
              }}
            >
              {ticker}
            </Typography>
            {exchange && !isMobile && (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  backgroundColor: flatUIColors.surface,
                  border: `1px solid ${flatUIColors.border}`,
                  borderRadius: 0,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    ...flatUITypography.label,
                    color: flatUIColors.textSecondary,
                    fontSize: "0.65rem",
                  }}
                >
                  {exchange}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Divider - Desktop only */}
        {!isMobile && (
          <Box
            sx={{
              width: "1px",
              height: "40px",
              backgroundColor: flatUIColors.border,
              mx: 1,
            }}
          />
        )}

        {/* Price Information - Desktop (inline) */}
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            {/* Current Price */}
            <Box>
              <Typography
                sx={{
                  ...flatUITypography.caption,
                  color: flatUIColors.textSecondary,
                  fontSize: "0.7rem",
                }}
              >
                Current
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: flatUIColors.textPrimary,
                }}
              >
                {formatPrice(currentPrice)}
              </Typography>
            </Box>

            {/* Fair Value - use formatDisplayPrice since it's a calculated value already in pounds */}
            <Box>
              <Typography
                sx={{
                  ...flatUITypography.caption,
                  color: flatUIColors.textSecondary,
                  fontSize: "0.7rem",
                }}
              >
                {fairValueLabel}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: valuationColor,
                }}
              >
                {formatDisplayPrice(fairValue)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Right Section - Help Button (Desktop) */}
      {!isMobile && onHelpClick && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<HelpOutlineIcon sx={{ fontSize: 18 }} />}
          onClick={onHelpClick}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            color: flatUIColors.textSecondary,
            borderColor: flatUIColors.border,
            backgroundColor: "transparent",
            px: 1.5,
            py: 0.5,
            minHeight: 32,
            "&:hover": {
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              backgroundColor: "transparent",
            },
          }}
        >
          Help
        </Button>
      )}

      {/* Right Section - Mobile Price Information (stacked) */}
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          {/* Current Price */}
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                color: flatUIColors.textSecondary,
                fontSize: "0.55rem",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                lineHeight: 1,
              }}
            >
              Current
            </Typography>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: flatUIColors.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {formatPrice(currentPrice)}
            </Typography>
          </Box>

          {/* Fair Value - use formatDisplayPrice since it's a calculated value already in pounds */}
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                color: flatUIColors.textSecondary,
                fontSize: "0.55rem",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                lineHeight: 1,
              }}
            >
              Fair Value
            </Typography>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: valuationColor,
                lineHeight: 1.2,
              }}
            >
              {formatDisplayPrice(fairValue)}
            </Typography>
          </Box>

          {/* Help Button - Mobile */}
          {onHelpClick && (
            <IconButton
              size="small"
              onClick={onHelpClick}
              sx={{
                color: flatUIColors.textSecondary,
                ml: 0.5,
                "&:hover": {
                  color: theme.palette.primary.main,
                  backgroundColor: flatUIColors.surface,
                },
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CompactProjectionsHeader;
